/**
 * 用户相关的工具函数
 */

/**
 * 从用户对象或字符串中提取用户名
 * @param {Object|string} user - 用户对象或用户名字符串
 * @param {string} fallback - 默认值，当用户信息不可用时使用
 * @returns {string} 用户名字符串
 */
export const getUserDisplayName = (user, fallback = '匿名用户') => {
  if (!user) {
    return fallback;
  }
  
  if (typeof user === 'string') {
    return user || fallback;
  }
  
  if (typeof user === 'object') {
    // 优先使用 name，然后是 username，最后是 fallback
    return user.name || user.username || fallback;
  }
  
  return fallback;
};

/**
 * 从作者对象或字符串中提取作者名
 * @param {Object|string} author - 作者对象或作者名字符串
 * @param {string} fallback - 默认值，当作者信息不可用时使用
 * @returns {string} 作者名字符串
 */
export const getAuthorDisplayName = (author, fallback = '匿名作者') => {
  return getUserDisplayName(author, fallback);
};

/**
 * 生成用户头像文本（首字母）
 * @param {Object|string} user - 用户对象或用户名字符串
 * @returns {string} 头像文本（大写首字母）
 */
export const getUserAvatarText = (user) => {
  const displayName = getUserDisplayName(user, 'U');
  return displayName.charAt(0).toUpperCase();
};

/**
 * 检查用户对象是否有效
 * @param {any} user - 要检查的用户数据
 * @returns {boolean} 是否为有效的用户对象
 */
export const isValidUser = (user) => {
  if (!user) return false;
  
  if (typeof user === 'string') {
    return user.trim().length > 0;
  }
  
  if (typeof user === 'object') {
    return !!(user.name || user.username || user.id);
  }
  
  return false;
};

/**
 * 格式化用户信息用于显示
 * @param {Object|string} user - 用户对象或用户名字符串
 * @returns {Object} 格式化后的用户信息
 */
export const formatUserForDisplay = (user) => {
  const displayName = getUserDisplayName(user);
  const avatarText = getUserAvatarText(user);
  
  return {
    displayName,
    avatarText,
    isValid: isValidUser(user),
    isObject: typeof user === 'object'
  };
};
